import { CSSProperties, useEffect, useState } from 'react';
import { dungeonService } from '../../services/DungeonService';
import { msToTicks } from '../../utils/timing.utils';
import { Icon, frameExists } from './Icon';
import './BuffList.scss';

interface BuffView {
  id: string;
  name: string;
  icon: string;
  stacks: number;
  /** Remaining duration fraction (1 = fresh, 0 = expiring); null when permanent. */
  remaining: number | null;
}

export const BuffList = () => {
  const [buffs, setBuffs] = useState<BuffView[]>([]);

  useEffect(() => {
    const tick = () => {
      const player = dungeonService.getPlayer();
      if (!player) {
        setBuffs([]);
        return;
      }
      const views: BuffView[] = [];
      for (const b of player.getActiveBuffs()) {
        if (b.def.internal || !b.def.icon || !frameExists(b.def.icon)) {
          continue;
        }
        const totalTicks = b.def.durationMs ? msToTicks(b.def.durationMs) : 0;
        const remaining =
          totalTicks > 0 && b.remainingTicks !== undefined
            ? Math.max(0, Math.min(1, b.remainingTicks / totalTicks))
            : null;
        views.push({
          id: b.def.id,
          name: b.def.name,
          icon: b.def.icon,
          stacks: b.stacks ?? 1,
          remaining,
        });
      }
      setBuffs(views);
    };
    tick();
    const interval = setInterval(tick, 100);
    return () => clearInterval(interval);
  }, []);

  if (buffs.length === 0) {
    return null;
  }

  return (
    <div className="buff-list">
      {buffs.map((b) => {
        const timed = b.remaining !== null;
        return (
          <div
            key={b.id}
            className={`buff-list-item ${
              timed ? 'buff-list-item--timed' : 'buff-list-item--static'
            }`}
            style={{ '--remaining': b.remaining ?? 1 } as CSSProperties}
            title={b.name}
          >
            <div className="buff-list-icon">
              <Icon sprite={b.icon} fill />
            </div>
            {b.stacks > 1 && (
              <span className="buff-list-stacks">{b.stacks}</span>
            )}
          </div>
        );
      })}
    </div>
  );
};
